import React from 'react';
import { motion } from 'motion/react';
import { Brain, Activity, Users, ChevronRight, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { format, isSameDay } from 'date-fns';
import { PITCH_COUNT_RULESETS, getPitcherEligibility, buildCustomTournamentRules } from '../lib/pitch-count-rules';
import { cn } from '../lib/utils';

interface BetweenGamesAdvisorProps {
  game: any;
  team: any;
  players: any[];
  games: any[];
  /** The resolved tournament doc, when this game belongs to one. Lets the
      advisor honor tournament-level pitch rules (incl. per-tournament custom). */
  tournament?: any;
  onClose: () => void;
}

export const BetweenGamesAdvisor = ({ game, team, players, games, tournament, onClose }: BetweenGamesAdvisorProps) => {
  // Tournament-aware ruleset: when this game is in a tournament, the tournament's
  // override (e.g. 'custom') wins over the season ruleset. Mirrors the live engine.
  const rulesetKey = (game.tournamentId
    ? (tournament?.pitchCountRulesetOverride || team.settings?.tournamentOrg)
    : null)
    || team.settings?.pitchCountRuleset
    || 'little_league';
  const ruleset = PITCH_COUNT_RULESETS[rulesetKey] || PITCH_COUNT_RULESETS.little_league;
  const ageGroup = team.settings?.pitchCountAgeGroup || team.ageGroup || '12U';
  const today = new Date(game.date);

  // Custom rules: per-tournament values when present, else season defaults.
  const effectiveCustomRules = buildCustomTournamentRules(ruleset.id === 'custom', tournament, team.settings);
  // The daily cap the advisor compares against — custom dailyMax when the ruleset
  // is custom, otherwise the age-group's threshold from the chosen ruleset.
  const dailyMaxFor = (): number => {
    if (ruleset.id === 'custom' && effectiveCustomRules?.dailyMax != null) return effectiveCustomRules.dailyMax;
    const ar = ruleset.ageGroups[ageGroup] || ruleset.ageGroups['12U'] || { dailyMax: 85 };
    return ar.dailyMax ?? 85;
  };
  const dailyMax = dailyMaxFor();

  // Calculate cumulative daily pitch counts
  const dailyPitchCounts: Record<string, number> = {};
  games.forEach(g => {
    if (g.tournamentId === game.tournamentId && isSameDay(new Date(g.date), today) && g.pitchCounts) {
      Object.entries(g.pitchCounts).forEach(([pid, count]) => {
        dailyPitchCounts[pid] = (dailyPitchCounts[pid] || 0) + (count as number);
      });
    }
  });

  const availablePitchers = players.filter(p => {
    const eligibility = getPitcherEligibility(p.pitchingLog || [], ruleset, ageGroup, today, 0, game.tournamentId, true, effectiveCustomRules);
    const dailyCount = dailyPitchCounts[p.id] || 0;
    return eligibility.eligible && dailyCount < dailyMax;
  }).sort((a, b) => (b.attributes?.pitching?.velocity || 0) - (a.attributes?.pitching?.velocity || 0));

  const restingPitchers = players.filter(p => {
    const eligibility = getPitcherEligibility(p.pitchingLog || [], ruleset, ageGroup, today, 0, game.tournamentId, true, effectiveCustomRules);
    return !eligibility.eligible;
  });

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 z-[100] bg-surface flex flex-col"
    >
      <header className="safe-top p-6 border-b border-hairline flex items-center justify-between bg-surface backdrop-blur-xl sticky top-0 z-20">
        <div className="flex items-center space-x-4">
          <div className="w-10 h-10 bg-gold-soft rounded-xl flex items-center justify-center">
            <Brain className="w-6 h-6 text-gold-ink" />
          </div>
          <div className="flex flex-col">
            <h2 className="text-xl font-black italic uppercase">Between-Games Advisor</h2>
            <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Tactical Review & Next Steps</p>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-6 space-y-8">
        {/* Pitching Summary */}
        <section className="space-y-4">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-xs font-semibold text-ink-2 uppercase tracking-widest">Pitching Inventory</h3>
            <span className="text-[10px] font-bold text-accent-ink uppercase">{availablePitchers.length} Available</span>
          </div>
          
          <div className="grid gap-3">
            {availablePitchers.slice(0, 3).map(p => {
              const dailyCount = dailyPitchCounts[p.id] || 0;
              const remaining = Math.max(0, dailyMax - dailyCount);

              return (
                <div key={p.id} className="bg-canvas p-4 rounded-2xl border border-hairline flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-raised rounded-xl flex items-center justify-center">
                      <span className="text-sm font-black text-ink-2">#{p.number}</span>
                    </div>
                    <div>
                      <h4 className="font-black uppercase text-sm">{p.name}</h4>
                      <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
                        {remaining} Pitches Remaining Today
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-black text-accent-ink italic">{p.attributes?.pitching?.velocity || 0} MPH</div>
                    <div className="text-[8px] font-bold text-ink-2 uppercase">Velo Rating</div>
                  </div>
                </div>
              );
            })}
          </div>

          {restingPitchers.length > 0 && (
            <div className="bg-gold-soft border border-amber-500/10 p-4 rounded-2xl flex items-start space-x-3">
              <AlertTriangle className="w-4 h-4 text-gold-ink mt-0.5" />
              <div>
                <p className="text-[10px] font-semibold text-gold-ink uppercase tracking-widest mb-1">Mandatory Rest</p>
                <p className="text-[10px] text-ink-2 font-medium">
                  {restingPitchers.length} pitchers are ineligible for the next game due to rest requirements.
                </p>
              </div>
            </div>
          )}
        </section>

        {/* Fairness Advisor */}
        <section className="space-y-4">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-xs font-semibold text-ink-2 uppercase tracking-widest">Fairness Check</h3>
            <span className="text-[10px] font-bold text-blue-500 uppercase">Pool Play Mode</span>
          </div>

          <div className="bg-canvas p-6 rounded-3xl border border-hairline space-y-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-500/10 rounded-2xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h4 className="font-black uppercase text-sm">Bench Rotation Recommendation</h4>
                <p className="text-[10px] text-ink-2 font-semibold uppercase tracking-widest">Maximize Development</p>
              </div>
            </div>

            <p className="text-xs text-ink-2 leading-relaxed">
              Based on the last game, 3 players were on the bench for more than 2 innings. 
              In the next game, prioritize starting them in the infield to maintain fairness and engagement.
            </p>

            <div className="flex items-center space-x-2">
              <CheckCircle2 className="w-4 h-4 text-accent-ink" />
              <span className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Fairness Score: 92% (Excellent)</span>
            </div>
          </div>
        </section>

        {/* Tactical Note */}
        <div className="bg-canvas border border-ink/10 p-6 rounded-3xl">
          <h4 className="text-[10px] font-semibold text-accent-ink uppercase tracking-widest mb-2 flex items-center space-x-2">
            <Activity className="w-3 h-3" />
            <span>Coach's Note</span>
          </h4>
          <p className="text-sm text-ink-2 italic">
            "We're in a great spot for the next game. Pitching is deep and the kids are rotating well. 
            Keep the energy high in the dugout. That's where we win the mental game."
          </p>
        </div>
      </div>

      <div className="p-6 border-t border-hairline bg-surface">
        <button 
          onClick={onClose}
          className="w-full py-4 bg-ink text-surface font-black rounded-2xl uppercase italic flex items-center justify-center space-x-2 hover:scale-105 transition-transform"
        >
          <span>Return to Tournament</span>
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </motion.div>
  );
};
